import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import {
  Alert,
  Keyboard,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { normalizeApiError } from '../../api/apiError';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ScreenHeader } from '../../components/ScreenHeader';
import { ErrorState } from '../../components/ErrorState';
import { FaceEnrollmentCamera } from '../../components/FaceEnrollmentCamera';
import { FaceGuideCorners } from '../../components/FaceGuideCorners';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ProfileAvatar } from '../../components/ProfileAvatar';
import { getFaceEnrollmentConfig } from '../../config/faceEnrollmentConfig';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { isWebPlatform, webModalCard, webModalOverlay } from '../../utils/webLayout';
import { getEmployeeDetails } from '../../services/employeeService';
import {
  addFaceSample,
  deleteFaceRegistration,
  ensureAzurePerson,
  getFaceDeletionStatus,
  getFaceRegistration,
  pollPersonGroupTrainingStatus,
  retryFaceDeletion,
  startPersonGroupTraining,
} from '../../services/employeeFaceRegistrationService';
import {
  ensureOrganizationAwsProvider,
  ensureOrganizationPersonGroupIfNeeded,
  trainPersonGroupAndWait,
} from '../../services/faceAdminService';
import type { EmployeeDetails } from '../../types/employee';
import type {
  EmployeeFaceSample,
  FaceRegistrationFlowState,
  FaceRegistrationStatus,
} from '../../types/faceRegistration';
import { formatDateTime } from '../../utils/dateTime';
import {
  ENROLLMENT_POSE_LABELS,
  ENROLLMENT_POSE_SHORT_LABELS,
  requiredPoseForSampleIndex,
} from '../../utils/faceEnrollmentPose';
import { faceRegistrationErrorMessage } from '../../utils/faceRegistrationErrors';
import { deleteTemporaryImage } from '../../utils/temporaryImage';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { OperationFeedbackHost } from '../../components/OperationFeedbackHost';
import { runOperation } from '../../utils/operationFeedback';

const INSTRUCTIONS = [
  'First image: look straight at the camera',
  'Second image: turn your head to the left',
  'Third image: turn your head to the right',
  'Keep your face clearly visible without a cap or sunglasses',
];

type Props = NativeStackScreenProps<RootStackParamList, 'EmployeeFaceRegistration'>;

type ScreenState = {
  flow: FaceRegistrationFlowState;
  snapshot: FaceRegistrationStatus | null;
  error: string | null;
};

type Action =
  | { type: 'set_flow'; flow: FaceRegistrationFlowState }
  | { type: 'loaded'; snapshot: FaceRegistrationStatus }
  | { type: 'failed'; message: string };

const SAMPLE_LABELS = [
  ENROLLMENT_POSE_LABELS.front,
  ENROLLMENT_POSE_LABELS.left,
  ENROLLMENT_POSE_LABELS.right,
];

function deriveFlow(snapshot: FaceRegistrationStatus): FaceRegistrationFlowState {
  const reg = snapshot.registration;
  if (reg.personStatus === 'DELETED') return 'DELETED';
  if (reg.deletionPending) return 'DELETE_PENDING';
  if (
    reg.retryAllowed ||
    ((reg.personStatus === 'ERROR' ||
      reg.syncStatus === 'FAILED' ||
      reg.syncStatus === 'ERROR') &&
      (reg.personStatus === 'DELETE_PENDING' || reg.syncStatus === 'DELETE_PENDING'))
  ) {
    return 'DELETION_FAILED';
  }
  if (!reg.azurePersonConfigured) return 'UNREGISTERED';
  if (reg.isReady) return 'READY';
  if (reg.trainingStatus === 'RUNNING') return 'TRAINING';
  if (
    reg.activeFaceCount >= 1 &&
    reg.providerTrainingRequired !== false &&
    reg.trainingStatus !== 'SUCCEEDED' &&
    reg.trainingStatus !== 'NOT_REQUIRED'
  ) {
    return 'TRAINING_REQUIRED';
  }
  return 'READY_FOR_SAMPLES';
}

function reducer(state: ScreenState, action: Action): ScreenState {
  switch (action.type) {
    case 'set_flow':
      return { ...state, flow: action.flow, error: null };
    case 'loaded':
      return {
        flow: deriveFlow(action.snapshot),
        snapshot: action.snapshot,
        error: null,
      };
    case 'failed':
      return { ...state, flow: 'ERROR', error: action.message };
    default:
      return state;
  }
}

function trainingLabel(status: string | null | undefined) {
  switch (status) {
    case 'NOT_STARTED':
      return 'Not started';
    case 'PENDING':
      return 'Training required';
    case 'RUNNING':
      return 'Training in progress';
    case 'SUCCEEDED':
      return 'Registration ready';
    case 'NOT_REQUIRED':
      return 'Training not required';
    case 'FAILED':
      return 'Training failed';
    default:
      return status || 'Unknown';
  }
}

function flowHeadline(flow: FaceRegistrationFlowState): string {
  switch (flow) {
    case 'UNREGISTERED':
      return 'Not registered';
    case 'CREATING_PERSON':
      return 'Setting up registration';
    case 'READY_FOR_SAMPLES':
    case 'CAPTURING_SAMPLE':
      return 'Capture samples';
    case 'UPLOADING_SAMPLE':
      return 'Uploading sample';
    case 'TRAINING_REQUIRED':
      return 'Training required';
    case 'TRAINING':
      return 'Training in progress';
    case 'READY':
      return 'Ready for attendance';
    case 'DELETE_PENDING':
      return 'Deletion pending';
    case 'DELETION_FAILED':
      return 'Deletion failed';
    case 'DELETED':
      return 'Registration deleted';
    case 'ERROR':
      return 'Something went wrong';
    default:
      return flow;
  }
}

function readinessTone(
  flow: FaceRegistrationFlowState,
  palette: ReturnType<typeof useAppColors>,
) {
  if (flow === 'READY') {
    return { backgroundColor: `${palette.success}22`, color: palette.success };
  }
  if (flow === 'ERROR' || flow === 'DELETION_FAILED' || flow === 'DELETED') {
    return { backgroundColor: `${palette.error}18`, color: palette.error };
  }
  if (flow === 'TRAINING' || flow === 'TRAINING_REQUIRED' || flow === 'DELETE_PENDING') {
    return { backgroundColor: `${palette.warning}18`, color: palette.warning };
  }
  return { backgroundColor: `${palette.info}14`, color: palette.info };
}

function formatRegisteredAt(value: string | null | undefined): string {
  if (!value) return '—';
  const formatted = formatDateTime(value);
  return formatted === 'Not available' ? value : formatted;
}

function formatCaptureSource(value: string | null | undefined): string {
  if (!value) return '—';
  return value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase());
}

function earliestCapture(faces: EmployeeFaceSample[]): string | null {
  const times = faces
    .map(face => face.capturedAt)
    .filter((value): value is string => Boolean(value))
    .map(value => ({ value, time: new Date(value).getTime() }))
    .filter(entry => Number.isFinite(entry.time))
    .sort((a, b) => a.time - b.time);
  return times[0]?.value ?? null;
}

function primaryCaptureSource(faces: EmployeeFaceSample[]): string | null {
  const primary = faces.find(face => face.isPrimary && face.captureSource);
  if (primary?.captureSource) return primary.captureSource;
  return faces.find(face => face.captureSource)?.captureSource ?? null;
}

export function EmployeeFaceRegistrationScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const employeeId = route.params.employeeId;
  const config = getFaceEnrollmentConfig();
  const [employee, setEmployee] = useState<EmployeeDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [state, dispatch] = useReducer(reducer, {
    flow: 'UNREGISTERED',
    snapshot: null,
    error: null,
  });
  const [showCamera, setShowCamera] = useState(false);
  const [cameraDismissed, setCameraDismissed] = useState(false);
  const [busyAction, setBusyAction] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [retryAllowed, setRetryAllowed] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [showCaptures, setShowCaptures] = useState(false);
  const ensuringRef = React.useRef(false);
  const suppressAutoEnsureRef = React.useRef(false);

  const themed = useMemo(
    () => ({
      section: { ...typography.sectionTitle, color: palette.textPrimary },
      value: { ...typography.body, color: palette.textPrimary },
      meta: { ...typography.caption, color: palette.textSecondary },
      hint: {
        ...typography.caption,
        color: palette.textSecondary,
        marginBottom: spacing.md,
      },
      error: { ...typography.body, color: palette.error, marginBottom: spacing.sm },
      statusMessage: {
        ...typography.body,
        color: palette.info,
        marginBottom: spacing.sm,
      },
      readyTitle: {
        ...typography.sectionTitle,
        color: palette.success,
        fontWeight: '700' as const,
      },
      readyBody: {
        ...typography.caption,
        color: palette.success,
      },
      profileName: {
        ...typography.sectionTitle,
        color: palette.textPrimary,
        fontWeight: '700' as const,
      },
      profileRole: {
        ...typography.caption,
        color: palette.textSecondary,
      },
      instructionItem: {
        ...typography.body,
        color: palette.textPrimary,
        flex: 1,
      },
      detailLabel: {
        ...typography.caption,
        color: palette.textMuted,
        fontWeight: '600' as const,
      },
      detailValue: {
        ...typography.body,
        color: palette.textPrimary,
        fontWeight: '600' as const,
      },
    }),
    [palette],
  );

  const load = useCallback(
    async (isRefresh = false) => {
      isRefresh ? setRefreshing(true) : setLoading(true);
      try {
        const [details, face] = await Promise.all([
          getEmployeeDetails(employeeId),
          getFaceRegistration(employeeId),
        ]);
        setEmployee(details);
        dispatch({ type: 'loaded', snapshot: face });
        if (face.registration.deletionPending || face.registration.personStatus === 'DELETED') {
          try {
            const deletion = await getFaceDeletionStatus(employeeId);
            setRetryAllowed(deletion.retryAllowed === true);
            if (deletion.personStatus === 'DELETED') {
              dispatch({
                type: 'loaded',
                snapshot: {
                  ...face,
                  registration: {
                    ...face.registration,
                    personStatus: 'DELETED',
                    azurePersonConfigured: false,
                    isReady: false,
                    deletionPending: false,
                    retryAllowed: deletion.retryAllowed,
                  },
                },
              });
            } else if (
              deletion.retryAllowed ||
              deletion.personStatus === 'DELETE_PENDING' ||
              deletion.syncStatus === 'FAILED' ||
              deletion.syncStatus === 'DELETE_PENDING'
            ) {
              setRetryAllowed(deletion.retryAllowed);
              dispatch({
                type: 'set_flow',
                flow:
                  deletion.personStatus === 'DELETE_PENDING' ||
                  deletion.syncStatus === 'DELETE_PENDING'
                    ? 'DELETE_PENDING'
                    : 'DELETION_FAILED',
              });
            }
          } catch {
            // Deletion status is optional when registration is healthy.
          }
        } else {
          setRetryAllowed(false);
        }
      } catch (caught) {
        dispatch({
          type: 'failed',
          message: faceRegistrationErrorMessage(
            normalizeApiError(caught).code,
            'Unable to load face registration.',
          ),
        });
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [employeeId],
  );

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  const registration = state.snapshot?.registration;
  const faces = state.snapshot?.faces ?? [];
  const sampleCount = registration?.activeFaceCount ?? 0;
  const recommendedCount = config.recommendedSampleCount;
  const sampleLimitReached =
    registration?.maxFaceSamples != null &&
    sampleCount >= registration.maxFaceSamples;
  const needsRecommendedSamples =
    !sampleLimitReached && sampleCount < recommendedCount;
  const showReadySummary =
    Boolean(registration?.isReady) && !needsRecommendedSamples;
  const requiredPose =
    sampleCount < recommendedCount
      ? requiredPoseForSampleIndex(sampleCount)
      : undefined;
  const guidanceLabel =
    requiredPose != null
      ? ENROLLMENT_POSE_LABELS[requiredPose]
      : SAMPLE_LABELS[Math.min(sampleCount, SAMPLE_LABELS.length - 1)] ??
        'Additional sample';
  const statusTone = readinessTone(state.flow, palette);
  const providerLabel = registration?.biometricProvider
    ? registration.biometricProvider.replace(/_/g, ' ')
    : null;

  const canCapture =
    !busyAction &&
    !sampleLimitReached &&
    !showReadySummary &&
    registration?.azurePersonConfigured === true &&
    state.flow !== 'DELETED' &&
    state.flow !== 'DELETE_PENDING' &&
    state.flow !== 'DELETION_FAILED' &&
    state.flow !== 'TRAINING' &&
    state.flow !== 'CREATING_PERSON' &&
    state.flow !== 'UPLOADING_SAMPLE' &&
    state.flow !== 'UNREGISTERED';

  // Keep collecting the recommended 3 angles even if the provider already
  // marks registration Ready after the first synced sample (common on AWS).
  const shouldAutoOpenCamera =
    canCapture &&
    needsRecommendedSamples &&
    (state.flow === 'READY_FOR_SAMPLES' ||
      state.flow === 'CAPTURING_SAMPLE' ||
      state.flow === 'READY');

  const headerTitle = showReadySummary
    ? 'Face Already Registered'
    : 'Face Registration';
  const employeeLabel = employee
    ? `${employee.employeeNumber} - ${employee.displayName}`
    : `Employee ${employeeId}`;
  const employeeRole = employee?.designation || employee?.department || 'Employee';
  const registeredOn = formatRegisteredAt(earliestCapture(faces));
  const registeredBy = formatCaptureSource(primaryCaptureSource(faces));
  const gallerySlots = Math.max(recommendedCount, sampleCount, faces.length);

  function registrationActionError(caught: unknown, fallback?: string) {
    const apiError = normalizeApiError(caught);
    return faceRegistrationErrorMessage(
      apiError.code,
      apiError.message || fallback,
    );
  }

  async function ensurePersonForEnrollment() {
    if (busyAction || ensuringRef.current) return;
    suppressAutoEnsureRef.current = false;
    ensuringRef.current = true;
    setBusyAction(true);
    dispatch({ type: 'set_flow', flow: 'CREATING_PERSON' });
    setStatusMessage('');
    try {
      await ensureOrganizationAwsProvider();
      await ensureOrganizationPersonGroupIfNeeded();
      await ensureAzurePerson(employeeId);
      setCameraDismissed(false);
      setStatusMessage('Face registration is ready. Opening camera…');
      await load();
    } catch (caught) {
      dispatch({
        type: 'failed',
        message: registrationActionError(caught),
      });
    } finally {
      ensuringRef.current = false;
      setBusyAction(false);
    }
  }

  // Auto-create provider person when opening an unregistered employee.
  useEffect(() => {
    if (loading || busyAction || ensuringRef.current || suppressAutoEnsureRef.current) {
      return;
    }
    if (state.flow === 'UNREGISTERED') {
      ensurePersonForEnrollment().catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.flow, loading, busyAction]);

  // Auto-open in-app front camera as soon as enrollment can accept required samples.
  useEffect(() => {
    if (!shouldAutoOpenCamera || showCamera || cameraDismissed) return;
    dispatch({ type: 'set_flow', flow: 'CAPTURING_SAMPLE' });
    setShowCamera(true);
  }, [shouldAutoOpenCamera, showCamera, cameraDismissed]);

  async function startRegistration() {
    Alert.alert(
      'Start face registration',
      'This creates the employee face registration record used for enrollment.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Continue',
          onPress: () => {
            ensurePersonForEnrollment().catch(() => undefined);
          },
        },
      ],
    );
  }

  async function uploadSample(imageUri: string) {
    if (busyAction || sampleLimitReached) {
      await deleteTemporaryImage(imageUri);
      return;
    }
    setShowCamera(false);
    setBusyAction(true);
    dispatch({ type: 'set_flow', flow: 'UPLOADING_SAMPLE' });
    setStatusMessage('Uploading face sample…');
    try {
      await addFaceSample(employeeId, { imageUri });
      const next = await getFaceRegistration(employeeId);
      dispatch({ type: 'loaded', snapshot: next });
      const nextCount = next.registration.activeFaceCount;
      const hitSampleCap =
        next.registration.maxFaceSamples != null &&
        nextCount >= next.registration.maxFaceSamples;
      const collectedRecommended = nextCount >= config.recommendedSampleCount;
      if (collectedRecommended || hitSampleCap) {
        setCameraDismissed(true);
        setShowCamera(false);
        setStatusMessage(
          next.registration.isReady || collectedRecommended
            ? `All ${nextCount} face angles captured. Face registration is ready for attendance.`
            : `Sample ${nextCount} uploaded.`,
        );
      } else {
        setStatusMessage(
          `Sample ${nextCount} uploaded. Capture the next angle (${SAMPLE_LABELS[Math.min(nextCount, SAMPLE_LABELS.length - 1)]}).`,
        );
        setCameraDismissed(false);
        // Re-open camera for the next recommended sample when still needed.
        if (next.registration.azurePersonConfigured) {
          setShowCamera(true);
          dispatch({ type: 'set_flow', flow: 'CAPTURING_SAMPLE' });
        }
      }
    } catch (caught) {
      dispatch({
        type: 'failed',
        message: registrationActionError(caught),
      });
      setStatusMessage('');
      setCameraDismissed(false);
      setShowCamera(true);
    } finally {
      setBusyAction(false);
      await deleteTemporaryImage(imageUri);
    }
  }

  async function startTraining() {
    if (busyAction) return;
    setBusyAction(true);
    dispatch({ type: 'set_flow', flow: 'TRAINING' });
    setStatusMessage('Training in progress');
    try {
      try {
        const waited = await trainPersonGroupAndWait();
        if (waited.trainingStatus === 'SUCCEEDED') {
          setStatusMessage('Face registration is ready for attendance.');
        } else {
          setStatusMessage(`Training finished: ${waited.trainingStatus}`);
        }
      } catch (trainAndWaitError) {
        const code =
          normalizeApiError(trainAndWaitError).code ||
          (trainAndWaitError as { code?: string }).code;
        // Fall back to client-side train + poll when train-and-wait times out
        // or is unavailable for the active provider.
        if (code !== 'AZURE_TRAINING_TIMEOUT') {
          await startPersonGroupTraining();
        }
        const result = await pollPersonGroupTrainingStatus();
        if (result.trainingStatus === 'SUCCEEDED') {
          setStatusMessage('Face registration is ready for attendance.');
        } else if (result.trainingStatus === 'FAILED') {
          setStatusMessage('Training failed. Try again or contact support.');
        } else if (code === 'AZURE_TRAINING_TIMEOUT') {
          setStatusMessage('Training is still running. Check status again shortly.');
        }
      }
      await load();
    } catch (caught) {
      const code = normalizeApiError(caught).code || (caught as { code?: string }).code;
      dispatch({
        type: 'failed',
        message: faceRegistrationErrorMessage(code, normalizeApiError(caught).message),
      });
      if (code === 'AZURE_TRAINING_TIMEOUT') {
        setStatusMessage('Training is still running. Check status again shortly.');
      }
    } finally {
      setBusyAction(false);
    }
  }

  async function confirmDelete() {
    Keyboard.dismiss();
    if (!deleteReason.trim()) {
      setDeleteError('A reason is required.');
      return;
    }
    setDeleteError('');
    setBusyAction(true);
    try {
      const result = await runOperation('delete', () => deleteFaceRegistration(employeeId, deleteReason.trim()));
      suppressAutoEnsureRef.current = true;
      setDeleteOpen(false);
      setDeleteReason('');
      setStatusMessage(
        result.alreadyDeleted
          ? 'Face registration was already removed.'
          : 'Face registration removed.',
      );
      await load();
    } catch (caught) {
      setDeleteError(registrationActionError(caught, 'Face registration could not be removed.'));
    } finally {
      setBusyAction(false);
    }
  }

  async function checkDeletionStatus() {
    setBusyAction(true);
    try {
      const status = await getFaceDeletionStatus(employeeId);
      setRetryAllowed(status.retryAllowed === true);
      setStatusMessage(
        status.personStatus === 'DELETED'
          ? 'Deleted'
          : status.retryAllowed
            ? 'Deletion failed'
            : 'Deletion pending',
      );
      await load();
    } catch (caught) {
      setStatusMessage(registrationActionError(caught));
    } finally {
      setBusyAction(false);
    }
  }

  async function retryDeletion() {
    if (!retryAllowed || busyAction) return;
    setBusyAction(true);
    try {
      await retryFaceDeletion(employeeId);
      setStatusMessage('Deletion retry completed.');
      await load();
    } catch (caught) {
      setStatusMessage(registrationActionError(caught));
      await load();
    } finally {
      setBusyAction(false);
    }
  }

  return (
    <PermissionGuard
      permission={PERMISSIONS.FACE_REGISTER}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !state.snapshot ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Face Registration"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading face registration" />
        </AppScreen>
      ) : state.flow === 'ERROR' && !state.snapshot ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Face Registration"
            onBack={navigation.goBack}
          />
          <ErrorState message={state.error || 'Unable to load face registration.'} onRetry={() => load()} />
        </AppScreen>
      ) : (
        <AppScreen
          scroll
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => load(true)}
              tintColor={palette.primary}
            />
          }
        >
          <ScreenHeader
            title={headerTitle}
            onBack={navigation.goBack}
          />
          <View style={styles.body}>

            <AppCard style={styles.profileCard}>
              <ProfileAvatar
                name={employee?.displayName || employeeLabel}
                size={56}
              />
              <View style={styles.profileCopy}>
                <Text style={themed.profileName} numberOfLines={2}>
                  {employeeLabel}
                </Text>
                <Text style={themed.profileRole} numberOfLines={1}>
                  {employeeRole}
                </Text>
              </View>
              <View
                style={[
                  styles.statusPill,
                  { backgroundColor: statusTone.backgroundColor },
                ]}
              >
                <Text
                  accessibilityLabel={`Screen state: ${state.flow}`}
                  style={[styles.statusPillText, { color: statusTone.color }]}
                >
                  {registration?.isReady ? 'Ready' : flowHeadline(state.flow)}
                </Text>
              </View>
            </AppCard>

            {!showReadySummary ? (
              <AppCard style={styles.card}>
                <Text style={themed.section}>Instructions</Text>
                <View style={styles.instructionList}>
                  {INSTRUCTIONS.map((item, index) => (
                    <View key={item} style={styles.instructionRow}>
                      <View
                        style={[
                          styles.instructionIndex,
                          { backgroundColor: `${palette.primary}12` },
                        ]}
                      >
                        <Text
                          style={[
                            styles.instructionIndexText,
                            { color: palette.primary },
                          ]}
                        >
                          {index + 1}
                        </Text>
                      </View>
                      <Text style={themed.instructionItem}>{item}</Text>
                    </View>
                  ))}
                </View>
              </AppCard>
            ) : null}

            {statusMessage ? (
              <Text accessibilityRole="alert" style={themed.statusMessage}>
                {statusMessage}
              </Text>
            ) : null}
            {state.error ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {state.error}
              </Text>
            ) : null}

            {state.flow === 'UNREGISTERED' ||
            (state.flow === 'ERROR' && !registration?.azurePersonConfigured) ? (
              <AppButton
                title="Start Face Registration"
                onPress={startRegistration}
                loading={busyAction}
                disabled={busyAction}
              />
            ) : null}

            {state.flow === 'DELETED' ? (
              <>
                <Text style={themed.hint}>
                  This registration is deleted. Capture is disabled until a new registration is started.
                </Text>
                <AppButton
                  title="Start Face Registration"
                  onPress={startRegistration}
                  loading={busyAction}
                  disabled={busyAction}
                />
              </>
            ) : null}

            {showReadySummary ? (
              <>
                <View
                  style={[
                    styles.readyBanner,
                    {
                      backgroundColor: `${palette.success}14`,
                      borderColor: `${palette.success}33`,
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.readyIcon,
                      {
                        borderColor: palette.success,
                        backgroundColor: `${palette.success}18`,
                      },
                    ]}
                  >
                    <Text style={[styles.readyIconText, { color: palette.success }]}>
                      ✓
                    </Text>
                  </View>
                  <View style={styles.readyCopy}>
                    <Text style={themed.readyTitle}>Face Already Registered</Text>
                    <Text style={themed.readyBody}>
                      This employee has already registered face data.
                    </Text>
                    <Text style={[themed.readyBody, { marginTop: spacing.xs }]}>
                      Face registration is ready for attendance.
                    </Text>
                  </View>
                </View>

                <AppCard style={styles.previewCard}>
                  <View
                    style={[
                      styles.registeredPreview,
                      { backgroundColor: `${palette.primary}08` },
                    ]}
                  >
                    <ProfileAvatar
                      name={employee?.displayName || employeeLabel}
                      size={120}
                    />
                    <FaceGuideCorners inset={18} size={32} />
                  </View>
                </AppCard>

                <AppCard style={styles.detailsCard}>
                  <View style={styles.detailRow}>
                    <Text style={themed.detailLabel}>Registered On</Text>
                    <Text style={themed.detailValue}>{registeredOn}</Text>
                  </View>
                  <View
                    style={[styles.detailDivider, { backgroundColor: palette.border }]}
                  />
                  <View style={styles.detailRow}>
                    <Text style={themed.detailLabel}>Registered By</Text>
                    <Text style={themed.detailValue}>{registeredBy}</Text>
                  </View>
                  <View
                    style={[styles.detailDivider, { backgroundColor: palette.border }]}
                  />
                  <View style={styles.detailRow}>
                    <Text style={themed.detailLabel}>Total Captures</Text>
                    <Text style={themed.detailValue}>
                      {sampleCount} {sampleCount === 1 ? 'Image' : 'Images'}
                    </Text>
                  </View>
                </AppCard>

                <AppButton
                  title="View Captures"
                  onPress={() => setShowCaptures(current => !current)}
                  accessibilityLabel="View Captures"
                />
              </>
            ) : null}

            {canCapture ? (
              <AppCard style={styles.captureCard}>
                <Text style={themed.meta}>
                  {sampleCount} of {recommendedCount} recommended samples
                </Text>
                {sampleLimitReached ? (
                  <Text style={themed.error}>
                    The maximum number of samples has been reached.
                  </Text>
                ) : showCamera ? (
                  <FaceEnrollmentCamera
                    guidanceLabel={guidanceLabel}
                    requiredPose={requiredPose}
                    disabled={busyAction}
                    autoLaunch
                    compact
                    onCaptured={uploadSample}
                    onCancel={() => {
                      setShowCamera(false);
                      setCameraDismissed(true);
                    }}
                  />
                ) : (
                  <AppButton
                    title="Capture Face"
                    onPress={() => {
                      setCameraDismissed(false);
                      dispatch({ type: 'set_flow', flow: 'CAPTURING_SAMPLE' });
                      setShowCamera(true);
                    }}
                    disabled={busyAction}
                    accessibilityLabel="Capture face sample"
                  />
                )}
              </AppCard>
            ) : null}

            {(canCapture || showCaptures || (!showReadySummary && faces.length > 0)) && (
              <AppCard style={styles.card}>
                <Text style={themed.section}>
                  Captured Images ({sampleCount || faces.length})
                </Text>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.galleryRow}
                >
                  {Array.from({ length: gallerySlots }).map((_, index) => {
                    const face = faces[index];
                    const filled = Boolean(face) || index < sampleCount;
                    return (
                      <View
                        key={`capture-slot-${index}`}
                        style={[
                          styles.galleryTile,
                          {
                            backgroundColor: filled
                              ? `${palette.primary}10`
                              : palette.background,
                            borderColor: filled ? `${palette.success}55` : palette.border,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.galleryTileLabel,
                            {
                              color: filled
                                ? palette.primary
                                : palette.textMuted,
                            },
                          ]}
                        >
                          {index < 3
                            ? ENROLLMENT_POSE_SHORT_LABELS[
                                requiredPoseForSampleIndex(index)
                              ]
                            : face?.sampleNumber ?? index + 1}
                        </Text>
                        {filled ? (
                          <View
                            style={[
                              styles.galleryCheck,
                              { backgroundColor: palette.success },
                            ]}
                          >
                            <Text style={styles.galleryCheckText}>✓</Text>
                          </View>
                        ) : null}
                      </View>
                    );
                  })}
                </ScrollView>

                {(showCaptures || !showReadySummary) && faces.length > 0 ? (
                  <View style={styles.sampleMetaList}>
                    {faces.map((face: EmployeeFaceSample, index) => (
                      <View
                        key={`${face.sampleNumber ?? index}-${face.captureSource ?? 'sample'}`}
                        style={[styles.sample, { borderTopColor: palette.border }]}
                      >
                        <View style={styles.sampleHeader}>
                          <Text style={themed.value}>
                            Sample {face.sampleNumber ?? index + 1}
                          </Text>
                          <Text
                            style={[
                              styles.sampleBadge,
                              {
                                color: face.isActive
                                  ? palette.success
                                  : palette.textMuted,
                                backgroundColor: face.isActive
                                  ? `${palette.success}18`
                                  : palette.border,
                              },
                            ]}
                          >
                            {face.isActive === null
                              ? 'Unknown'
                              : face.isActive
                                ? 'Active'
                                : 'Inactive'}
                          </Text>
                        </View>
                        <Text style={themed.meta}>
                          Source: {formatCaptureSource(face.captureSource)}
                        </Text>
                        <Text style={themed.meta}>
                          Captured: {formatRegisteredAt(face.capturedAt)}
                        </Text>
                      </View>
                    ))}
                  </View>
                ) : null}

                {!showReadySummary && faces.length === 0 ? (
                  <Text style={themed.meta}>
                    Captured samples will appear here after each successful photo.
                  </Text>
                ) : null}
              </AppCard>
            )}

            {(state.flow === 'TRAINING_REQUIRED' ||
              registration?.trainingStatus === 'PENDING' ||
              registration?.trainingStatus === 'FAILED' ||
              (sampleCount >= 1 &&
                !registration?.isReady &&
                registration?.providerTrainingRequired !== false &&
                registration?.trainingStatus !== 'RUNNING' &&
                registration?.trainingStatus !== 'NOT_REQUIRED' &&
                registration?.trainingStatus !== 'SUCCEEDED')) &&
            state.flow !== 'TRAINING' &&
            state.flow !== 'READY' &&
            registration?.azurePersonConfigured &&
            registration?.providerTrainingRequired !== false ? (
              <AppButton
                title="Start Training"
                onPress={startTraining}
                loading={busyAction}
                disabled={busyAction || sampleCount < 1}
              />
            ) : null}

            {state.flow === 'CREATING_PERSON' ? (
              <AppLoader message="Creating face registration…" />
            ) : null}

            {state.flow === 'UPLOADING_SAMPLE' ? (
              <AppLoader message="Uploading sample…" />
            ) : null}

            {state.flow === 'TRAINING' ? (
              <Text accessibilityLiveRegion="polite" style={themed.hint}>
                Training in progress…
              </Text>
            ) : null}

            {registration?.isReady && needsRecommendedSamples ? (
              <Text style={themed.statusMessage}>
                Registration is usable, but capture the remaining recommended
                angles for better match quality ({sampleCount} of {recommendedCount}).
              </Text>
            ) : null}

            {!showReadySummary ? (
              <AppCard style={styles.card}>
                <Text style={themed.section}>Registration details</Text>
                <View style={styles.metaGrid}>
                  <View style={styles.metaItem}>
                    <Text style={themed.meta}>Provider</Text>
                    <Text style={themed.value}>
                      {registration?.azurePersonConfigured
                        ? providerLabel || 'Configured'
                        : 'Not configured'}
                    </Text>
                  </View>
                  <View style={styles.metaItem}>
                    <Text style={themed.meta}>Training</Text>
                    <Text style={themed.value}>
                      {trainingLabel(registration?.trainingStatus)}
                    </Text>
                  </View>
                  <View style={styles.metaItem}>
                    <Text style={themed.meta}>Sync</Text>
                    <Text style={themed.value}>{registration?.syncStatus || '—'}</Text>
                  </View>
                  <View style={styles.metaItem}>
                    <Text style={themed.meta}>Samples</Text>
                    <Text style={themed.value}>
                      {sampleCount}
                      {registration?.maxFaceSamples != null
                        ? ` / ${registration.maxFaceSamples}`
                        : ` of ${recommendedCount} recommended`}
                    </Text>
                  </View>
                </View>
              </AppCard>
            ) : null}

            {(state.flow === 'DELETE_PENDING' || state.flow === 'DELETION_FAILED') && (
              <View style={styles.row}>
                <AppButton
                  title="Check Status"
                  variant="secondary"
                  onPress={checkDeletionStatus}
                  disabled={busyAction}
                  style={styles.half}
                />
                {retryAllowed ? (
                  <AppButton
                    title="Retry Deletion"
                    variant="danger"
                    onPress={retryDeletion}
                    disabled={busyAction}
                    style={styles.half}
                  />
                ) : null}
              </View>
            )}

            {registration?.azurePersonConfigured &&
            state.flow !== 'DELETED' &&
            state.flow !== 'DELETE_PENDING' ? (
              <AppButton
                title="Remove Face"
                variant="danger"
                onPress={() => {
                  setDeleteReason('');
                  setDeleteError('');
                  setDeleteOpen(true);
                }}
                disabled={busyAction}
                accessibilityLabel="Remove Face Registration"
              />
            ) : null}

            {deleteOpen ? (
              <Modal
                visible
                transparent
                animationType="fade"
                onRequestClose={() => {
                  if (!busyAction) setDeleteOpen(false);
                }}
              >
                <KeyboardAvoidingView
                  style={[
                    styles.modalOverlay,
                    isWebPlatform() ? webModalOverlay : null,
                  ]}
                  behavior={Platform.OS === 'ios' ? 'padding' : undefined}
                >
                  <Pressable
                    style={styles.modalBackdrop}
                    onPress={() => {
                      if (!busyAction) setDeleteOpen(false);
                    }}
                    accessibilityRole="button"
                    accessibilityLabel="Dismiss remove face"
                  />
                  <View
                    style={[
                      styles.modalCard,
                      isWebPlatform() ? webModalCard : null,
                      {
                        backgroundColor: palette.surface,
                        borderColor: palette.border,
                      },
                    ]}
                    accessibilityViewIsModal
                  >
                    <Text style={themed.section}>Remove Face Registration</Text>
                    <Text style={themed.hint}>
                      This removes the employee’s enrolled biometric identity from the
                      organization provider (AWS Rekognition or Azure Face) and disables
                      face attendance for this registration.
                    </Text>
                    <AppInput
                      label="Deletion reason"
                      value={deleteReason}
                      onChangeText={text => {
                        setDeleteReason(text);
                        if (deleteError) setDeleteError('');
                      }}
                      placeholder="Why is this registration being removed?"
                      error={deleteError || undefined}
                    />
                    <AppButton
                      title="Confirm removal"
                      variant="danger"
                      onPress={confirmDelete}
                      loading={busyAction}
                      disabled={busyAction}
                    />
                    <AppButton
                      title="Cancel"
                      variant="secondary"
                      onPress={() => setDeleteOpen(false)}
                      disabled={busyAction}
                    />
                  </View>
                </KeyboardAvoidingView>
                <OperationFeedbackHost />
              </Modal>
            ) : null}
          </View>
        </AppScreen>
      )}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg, gap: spacing.md },
  center: { flex: 1, justifyContent: 'center' },
  card: { marginBottom: spacing.xs, gap: spacing.md },
  profileCard: {
    marginBottom: spacing.xs,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  profileCopy: { flex: 1, gap: 2 },
  statusPill: {
    borderRadius: 999,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    maxWidth: '34%',
  },
  statusPillText: {
    ...typography.caption,
    fontWeight: '700',
    textAlign: 'center',
  },
  instructionList: { gap: spacing.md },
  instructionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  instructionIndex: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  instructionIndexText: {
    ...typography.caption,
    fontWeight: '700',
  },
  captureCard: { marginBottom: spacing.xs, gap: spacing.md },
  previewCard: {
    marginBottom: spacing.xs,
    padding: spacing.sm,
  },
  registeredPreview: {
    minHeight: 320,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  readyBanner: {
    borderWidth: 1,
    borderRadius: 16,
    padding: spacing.lg,
    gap: spacing.md,
    marginBottom: spacing.xs,
    flexDirection: 'row',
    alignItems: 'center',
  },
  readyIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  readyIconText: {
    fontSize: 20,
    fontWeight: '700',
  },
  readyCopy: { flex: 1, gap: 2 },
  detailsCard: {
    marginBottom: spacing.xs,
    paddingVertical: spacing.sm,
    gap: 0,
  },
  detailRow: {
    paddingVertical: spacing.md,
    gap: spacing.xs,
  },
  detailDivider: {
    height: StyleSheet.hairlineWidth,
  },
  galleryRow: {
    gap: spacing.sm,
    paddingVertical: spacing.xs,
  },
  galleryTile: {
    width: 72,
    height: 72,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  galleryTileLabel: {
    ...typography.sectionTitle,
    fontWeight: '700',
  },
  galleryCheck: {
    position: 'absolute',
    right: 6,
    bottom: 6,
    width: 18,
    height: 18,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
  },
  galleryCheckText: {
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: '700',
    lineHeight: 12,
  },
  sampleMetaList: { gap: spacing.sm },
  metaGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  metaItem: {
    width: '46%',
    gap: 2,
  },
  sample: {
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingTop: spacing.sm,
    gap: 2,
  },
  sampleHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  sampleBadge: {
    ...typography.caption,
    fontWeight: '700',
    overflow: 'hidden',
    borderRadius: 999,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
  },
  row: { flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.md },
  half: { flex: 1 },
  modalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  modalBackdrop: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(0,0,0,0.55)',
  },
  modalCard: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderWidth: 1,
    padding: spacing.lg,
    gap: spacing.md,
    marginHorizontal: spacing.md,
    marginBottom: spacing.lg,
  },
});
